// src/components/Auth/OTPVerificationPage.jsx
import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Flex,
  Text,
  Button,
  Input,
  HStack,
  useToast,
  useColorModeValue,
} from "@chakra-ui/react";
import {
  getUserPhone,
  saveAuth,
  saveCustomerToken,
  getRedirectUrl,
  clearRedirectUrl,
  clearAuth,
} from "../../utils/storage";
import { extractProjectLink } from "../../utils/linkNormalizer";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

// ─── Authentic link check ─────────────────────────────────────────────────
// Customer sirf tabhi app ke andar jaana chahiye jab uske paas ek genuine
// project link ho — home page pe jitne bhi module hain (planning, structure,
// elevation, interior) sab cover hain. Home page ("/") ya koi bhi aur path
// "authentic" nahi hai — isse home page (jisme saare modules dikhte hain)
// kisi ko bhi expose nahi hoga jab tak uske paas sahi link na ho.
//
// NOTE: pehle ye check ek strict regex tha jo SIRF "/module/PRJxxx" (path
// param) format accept karta tha. Isse email links (jo isi format mein
// hote hain) hamesha kaam karte the, lekin agar kisi doosre channel
// (WhatsApp jaisa) se link "/module?projectId=PRJxxx" (query param) format
// mein aata, to ye check use reject kar deta — asli valid link hote hue
// bhi "login link is not valid" error aata. Ab extractProjectLink() dono
// formats ko samajhta hai (utils/linkNormalizer.js), jo neeche handleVerify()
// ke andar directly use hota hai.

export default function OTPVerificationPage() {
  const [otp, setOtp] = useState(["", "", "", ""]);
  const [isLoading, setIsLoading] = useState(false);
  const [timer, setTimer] = useState(30);
  const [canResend, setCanResend] = useState(false);
  const navigate = useNavigate();
  const toast = useToast();
  const inputRefs = useRef([]);
  const phone = getUserPhone();

  useEffect(() => {
    if (!phone) {
      navigate("/login");
      return;
    }
    inputRefs.current[0]?.focus();
  }, [phone, navigate]);

  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(interval);
    } else {
      setCanResend(true);
    }
  }, [timer]);

  const handleChange = (index, value) => {
    if (value && !/^\d$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 3) {
      inputRefs.current[index + 1]?.focus();
    }

    if (newOtp.every((digit) => digit !== "") && index === 3) {
      handleVerify(newOtp.join(""));
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").slice(0, 4);
    if (!/^\d+$/.test(pastedData)) return;

    const newOtp = [...otp];
    pastedData.split("").forEach((digit, index) => {
      if (index < 4) newOtp[index] = digit;
    });
    setOtp(newOtp);

    const lastFilledIndex = Math.min(pastedData.length, 3);
    inputRefs.current[lastFilledIndex]?.focus();

    if (pastedData.length === 4) {
      handleVerify(pastedData);
    }
  };

  // Customer ke liye moveCardToInProgress API call
  // NEW: ab project "version" bhi URL me jaata hai —
  //   PUT /customer/form/moveCardToInProgress/:projectId/:version
  // Version redirect-link ke query param se milta hai (neeche call-site pe
  // nikaala jaata hai). Agar kisi link me version na ho (purana/incomplete
  // link), to purana bina-version wala URL hi hit hota hai — taaki koi
  // existing kaam karta hua link kabhi break na ho.
  const callMoveCardToInProgress = async (projectId, customerToken, version) => {
    try {
      const url = version
        ? `${API_BASE}/customer/form/moveCardToInProgress/${projectId}/${version}`
        : `${API_BASE}/customer/form/moveCardToInProgress/${projectId}`;

      const response = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "X-API-KEY": customerToken,
        },
        body: JSON.stringify({}),
      });
      const data = await response.json();
      console.log("📋 moveCardToInProgress (customer) response:", data);
    } catch (err) {
      // Non-blocking — error log karo lekin flow rok mat
      console.error("moveCardToInProgress (customer) failed:", err);
    }
  };

  const handleVerify = async (otpValue) => {
    const otpToVerify = otpValue || otp.join("");

    if (otpToVerify.length !== 4) {
      toast({
        title: "Invalid OTP",
        description: "Please enter a 4-digit OTP",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
      return;
    }

    setIsLoading(true);

    try {
      // Real API call — OTP verify karo
      const body = new URLSearchParams({
        otp: otpToVerify,
        mobile: phone,
        appcode: "RSFM",
        isd: "91",
        leadsource: "rsform",
      });

      const response = await fetch(
        `${API_BASE}/customer/me/veryfyotp`,
        {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: body.toString(),
        }
      );

      const data = await response.json();
      console.log("✅ OTP verify API response:", data);

      if (!response.ok || data.status === "error" || data.success === false) {
        throw new Error(data.message || data.msg || "Invalid OTP");
      }

      // Customer token save karo (API response se)
      const customerToken = data.token || data.api_token || data.data?.token || "";
      saveAuth(phone);
      if (customerToken) {
        saveCustomerToken(customerToken);
      }

      toast({
        title: "Verified!",
        description: "Mobile number verified successfully",
        status: "success",
        duration: 2000,
        isClosable: true,
        position: "top",
      });

      const redirectUrl = getRedirectUrl();
      // Chahe jo bhi format save hua ho (path param ya query param), ise
      // hamesha canonical "/module/PRJxxx" mein normalize karo — yehi ab
      // aage projectId nikaalne aur navigate karne dono ke liye use hoga.
      const canonicalUrl = extractProjectLink(redirectUrl);
      console.log("🔍 Checking for redirect URL:", redirectUrl, "→ canonical:", canonicalUrl);

      // Sirf ek genuine project link (/planning/:id, /structure/:id,
      // /elevation/:id, /interior/:id) pe hi aage jaane do. Bina authentic
      // link ke OTP verify ho jaaye to bhi customer ko app ke andar
      // (home page) nahi jaane dena — session revoke karke login pe wapas.
      if (canonicalUrl) {
        // Project ID canonical URL se nikalo — planning, structure,
        // elevation, interior sab ke liye kaam karega,
        // e.g. /structure/PRJ202408240060, /elevation/PRJ202408240060
        const match = canonicalUrl.match(/^\/[a-zA-Z]+\/([^/?]+)/);
        const projectId = match ? decodeURIComponent(match[1]) : null;

        // Version canonical URL ke query string se nikalo — "version" primary
        // key hai (jaise Planning/Structure/Elevation/Interior FormPage me),
        // "rsVersion" fallback hai (jaise utils/api.js ke fetchFormData me
        // viewMode links ke liye hota hai). Dono me se koi na mile to empty
        // reh jaata hai aur purana URL format use hota hai.
        const canonicalQuery = canonicalUrl.split("?")[1] || "";
        const canonicalParams = new URLSearchParams(canonicalQuery);
        const linkVersion =
          canonicalParams.get("version") || canonicalParams.get("rsVersion") || "";

        if (projectId && customerToken) {
          await callMoveCardToInProgress(projectId, customerToken, linkVersion);
        }
        clearRedirectUrl();
        console.log("✈️ Redirecting to:", canonicalUrl);
        navigate(canonicalUrl);
      } else {
        console.log("🚫 No authentic project link found — revoking session, not going to home");
        clearRedirectUrl();
        clearAuth();
        toast({
          title: "Access Denied",
          description: "This login link is not valid. Please use the link shared with you to access your form.",
          status: "error",
          duration: 5000,
          isClosable: true,
          position: "top",
        });
        navigate("/login");
      }
    } catch (err) {
      console.error("OTP verify error:", err);
      toast({
        title: "Invalid OTP",
        description: err.message || "Please enter the correct OTP",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
      setOtp(["", "", "", ""]);
      inputRefs.current[0]?.focus();
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    setTimer(30);
    setCanResend(false);
    setOtp(["", "", "", ""]);
    inputRefs.current[0]?.focus();

    try {
      const body = new URLSearchParams({
        mobile: phone,
        isd: "91",
        appcode: "RSFM",
      });

      await fetch(`${API_BASE}/customer/me/loginotp`, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      toast({
        title: "OTP Resent",
        description: `New OTP sent to ${phone}`,
        status: "info",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
    } catch (err) {
      console.error("Resend OTP error:", err);
      toast({
        title: "Resend failed",
        description: "Please try again",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
    }
  };

  const handleBack = () => {
    navigate("/login");
  };

  return (
    <Flex
      minH="100vh"
      align="center"
      justify="center"
      bg={useColorModeValue("gray.100", "gray.900")}
    >
      <Box
        w="100%"
        maxW="400px"
        bg={useColorModeValue("white", "gray.800")}
        px={8}
        py={10}
        borderRadius="lg"
        boxShadow="lg"
      >
        <Box mb={8} textAlign="center">
          <Text fontSize="28px" fontWeight="700" color="gray.800" mb={2}>
            Verify OTP
          </Text>
          <Text fontSize="14px" color="gray.600" mb={1}>
            OTP sent to
          </Text>
          <Text fontSize="16px" fontWeight="600" color="cyan.600">
            {phone}
          </Text>
        </Box>

        <Box mb={6}>
          <HStack spacing={3} justify="center" onPaste={handlePaste}>
            {otp.map((digit, index) => (
              <Input
                key={index}
                ref={(el) => (inputRefs.current[index] = el)}
                type="tel"
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                textAlign="center"
                fontSize="24px"
                fontWeight="600"
                h="56px"
                w="48px"
                borderRadius="8px"
                borderColor="gray.300"
                _hover={{ borderColor: "cyan.400" }}
                _focus={{
                  borderColor: "cyan.500",
                  boxShadow: "0 0 0 1px #00B5D8",
                }}
              />
            ))}
          </HStack>
        </Box>

        <Box mb={6} textAlign="center">
          {!canResend ? (
            <Text fontSize="14px" color="gray.600">
              Resend OTP in:{" "}
              <Text as="span" color="cyan.600" fontWeight="600">
                {timer}s
              </Text>
            </Text>
          ) : (
            <Button
              variant="link"
              color="cyan.600"
              fontSize="14px"
              fontWeight="600"
              onClick={handleResend}
            >
              Resend OTP
            </Button>
          )}
        </Box>

        <Button
          w="100%"
          h="48px"
          bg="cyan.500"
          color="white"
          fontSize="16px"
          fontWeight="600"
          borderRadius="8px"
          onClick={() => handleVerify()}
          isLoading={isLoading}
          loadingText="Verifying..."
          _hover={{ bg: "cyan.600" }}
          _active={{ bg: "cyan.700" }}
          isDisabled={otp.some((digit) => digit === "")}
          mb={3}
        >
          Verify
        </Button>

        <Button
          w="100%"
          h="48px"
          variant="outline"
          borderColor="cyan.500"
          color="cyan.600"
          fontSize="16px"
          fontWeight="600"
          borderRadius="8px"
          onClick={handleBack}
          _hover={{ bg: "cyan.50" }}
        >
          Go Back
        </Button>

        <Box mt={6} textAlign="center">
          <Text fontSize="12px" color="gray.500">
            Didn&apos;t receive OTP? Please check your number or resend
          </Text>
        </Box>
      </Box>
    </Flex>
  );
}